import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Link, useHistory } from "react-router-dom";
import { TbCheck, TbChevronRight, TbSend } from "react-icons/tb";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../../Hooks/useBaasCapabilities";
import { useBaasPlatformCreateAccess } from "../../../../../Hooks/useBaasPlatformCreateAccess";
import { useToast } from "../../../../../Hooks/toast";
import {
  fetchBaasInstanceDetail,
  isBaasHmlInstance,
  updateBaasInstance,
} from "../../../../../Services/baasInstancesApi";
import { BAAS_HOMOLOGACAO_PATH } from "../../constants";
import {
  areRequiredHomologStepsDone,
  getHomologStepsForCapabilities,
  loadHomologState,
  needsBaasHomologacao,
  resolveBaasHomologPair,
} from "../baasHomologacao";

/**
 * Prod Settings / lane hint — no checklist.
 * Partner: CTA to HML wizard. Valut: primary "Aprovar produção" (override OK).
 */
export const BaasProdAwaitingHomologBanner = ({
  instance,
  instances = [],
  compact = false,
  onLiberated,
}) => {
  const history = useHistory();
  const { selectInstance, refreshInstances } = useBaasInstance();
  const { addToast } = useToast();
  const { isValutConfigAdmin } = useBaasPlatformCreateAccess();
  const { capabilities, canViewHomolog } = useBaasCapabilities();
  const [busyProd, setBusyProd] = useState(false);
  const [busyRequest, setBusyRequest] = useState(false);
  const [requestedAt, setRequestedAt] = useState(null);
  const [error, setError] = useState(null);

  const pair = useMemo(
    () => resolveBaasHomologPair(instances, instance),
    [instances, instance],
  );
  const production = pair.production || instance;
  const development = pair.development;
  const checklistKey =
    development?.id || development?.baasInstanceId || null;
  const hmlActive = Boolean(development && development.active !== false);

  const requiredDone = useMemo(() => {
    if (!checklistKey) return false;
    const steps = getHomologStepsForCapabilities(capabilities);
    const { checks } = loadHomologState(checklistKey);
    return areRequiredHomologStepsDone(checks, steps);
  }, [checklistKey, capabilities]);

  const prodLiberated = production?.isProduction === true;
  const canLiberateProd =
    Boolean(isValutConfigAdmin) &&
    Boolean(production) &&
    !prodLiberated &&
    !isBaasHmlInstance(instance);
  const canRequestProd =
    !isValutConfigAdmin &&
    Boolean(production) &&
    !prodLiberated &&
    !isBaasHmlInstance(instance);
  const isProdOverride =
    canLiberateProd && (!requiredDone || !hmlActive);

  useEffect(() => {
    if (!production || prodLiberated) {
      setRequestedAt(null);
      return undefined;
    }
    let cancelled = false;
    fetchBaasInstanceDetail(production)
      .then((detail) => {
        if (!cancelled) setRequestedAt(detail?.productionRequestedAt || null);
      })
      .catch(() => {
        if (!cancelled) setRequestedAt(null);
      });
    return () => {
      cancelled = true;
    };
  }, [production, prodLiberated]);

  if (!instance || isBaasHmlInstance(instance)) return null;
  if (prodLiberated) return null;
  if (!needsBaasHomologacao(instance, instances)) return null;

  const openHmlWizard = (event) => {
    event.preventDefault();
    if (!canViewHomolog) return;
    if (development?.id) {
      selectInstance?.(development.id);
    }
    history.push(BAAS_HOMOLOGACAO_PATH);
  };

  const handleRequestProd = async () => {
    if (!canRequestProd || !production || busyRequest) return;
    setBusyRequest(true);
    setError(null);
    try {
      await updateBaasInstance({
        instance: production,
        productionRequested: true,
      });
      setRequestedAt(new Date().toISOString());
      addToast({
        type: "success",
        title: intl.get("baas.homologacao.prodRequestSuccess"),
      });
    } catch (err) {
      setError(
        err?.detail ||
          err?.message ||
          intl.get("baas.homologacao.prodRequestError"),
      );
    } finally {
      setBusyRequest(false);
    }
  };
  const handleLiberateProd = async () => {
    if (!canLiberateProd || !production || busyProd) return;
    if (isProdOverride) {
      const confirmed = window.confirm(
        intl.get("baas.homologacao.prodOverrideConfirm"),
      );
      if (!confirmed) return;
    }
    setBusyProd(true);
    setError(null);
    try {
      await updateBaasInstance({
        instance: production,
        isProduction: true,
      });
      addToast({
        type: "success",
        title: intl.get("baas.homologacao.prodApproveSuccess"),
      });
      try {
        await refreshInstances?.();
      } catch {
        // ignore
      }
      onLiberated?.();
    } catch (err) {
      setError(
        err?.detail ||
          err?.message ||
          intl.get("baas.homologacao.prodApproveError"),
      );
    } finally {
      setBusyProd(false);
    }
  };

  if (compact) {
    return (
      <span className="inline-flex min-w-0 items-center gap-1 truncate text-[11px] font-semibold text-amber-800 dark:text-amber-200">
        {intl.get(
          isValutConfigAdmin
            ? "baas.homologacao.badgeProdBlocked"
            : "baas.homologacao.awaitingHmlTitle",
        )}
      </span>
    );
  }

  return (
    <section className="rounded-2xl border border-amber-200/80 bg-amber-50/40 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
      <h3 className="text-sm font-bold text-brand-dark dark:text-gray-100">
        {intl.get(
          isValutConfigAdmin
            ? "baas.homologacao.valutProdGatesTitle"
            : "baas.homologacao.awaitingHmlTitle",
        )}
      </h3>
      <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
        {intl.get(
          isValutConfigAdmin
            ? "baas.homologacao.valutProdGatesHint"
            : "baas.homologacao.awaitingHmlHint",
        )}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {canRequestProd ? (
          requestedAt ? (
            <p className="text-xs font-medium text-amber-800 dark:text-amber-200">
              {intl.get("baas.homologacao.prodRequested")}
            </p>
          ) : (
            <button
              type="button"
              disabled={busyRequest}
              onClick={handleRequestProd}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-[#e7a73f] bg-white px-4 text-xs font-semibold text-[#a66f1d] transition enabled:hover:bg-[#fff8f0] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-transparent"
            >
              <TbSend size={16} />
              {busyRequest
                ? intl.get("baas.homologacao.prodRequesting")
                : intl.get("baas.homologacao.prodRequest")}
            </button>
          )
        ) : null}
        {canLiberateProd ? (
          <button
            type="button"
            disabled={busyProd}
            onClick={handleLiberateProd}
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-[#e7a73f] px-4 text-xs font-semibold text-white transition enabled:hover:bg-[#d4962f] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <TbCheck size={16} />
            {busyProd
              ? intl.get("baas.homologacao.prodApproving")
              : intl.get("baas.homologacao.prodApprove")}
          </button>
        ) : null}
        {development && !isValutConfigAdmin ? (
          <button
            type="button"
            onClick={openHmlWizard}
            disabled={!canViewHomolog}
            title={
              canViewHomolog
                ? undefined
                : intl.get("baas.homologacao.switchForbiddenHml")
            }
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#a66f1d] hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
          >
            {intl.get("baas.homologacao.switchToHmlWizard")}
            <TbChevronRight size={14} />
          </button>
        ) : null}
        {development && isValutConfigAdmin ? (
          <button
            type="button"
            onClick={openHmlWizard}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-[#a66f1d] hover:underline dark:text-gray-400"
          >
            {intl.get("baas.homologacao.valutInspectHml")}
            <TbChevronRight size={12} />
          </button>
        ) : null}
        {!development && !isValutConfigAdmin ? (
          <Link
            to={BAAS_HOMOLOGACAO_PATH}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#a66f1d] hover:underline"
          >
            {intl.get("baas.homologacao.openWizard")}
            <TbChevronRight size={14} />
          </Link>
        ) : null}
      </div>
      {isProdOverride ? (
        <p className="mt-2 text-[11px] text-amber-800 dark:text-amber-200">
          {intl.get("baas.homologacao.prodOverrideHint")}
        </p>
      ) : null}
      {error ? (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
};
